import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/context/AuthContext";
import {
  FileText, Headset, AlertTriangle, Loader2, ArrowLeft, ShieldOff, MonitorPlay, Printer, Wrench, ShieldCheck, Clock,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FilePreviewDialog } from "@/components/order/FilePreviewDialog";

const REFRESH_MS = 15000;
const SLIDE_MS = 12000;
const fmtJam = (d) => d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
const fmtTanggal = (t) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(t || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : (t || "-");
};

const SLIDES = [
  { key: "ba", label: "BA Pekerjaan", icon: FileText },
  { key: "helpdesk", label: "Helpdesk", icon: Headset },
  // PM masih develop — disembunyikan dari Live Monitor
];

const BA_BADGE = {
  "IN PROGRESS": "bg-indigo-100 text-indigo-700 border-indigo-200",
  REPORT: "bg-blue-100 text-blue-700 border-blue-200",
  APPROVED: "bg-emerald-100 text-emerald-700 border-emerald-200",
  REJECT: "bg-rose-100 text-rose-700 border-rose-200",
  REVISI: "bg-amber-100 text-amber-700 border-amber-200",
};
const HD_BADGE = {
  OPEN: "bg-blue-100 text-blue-700 border-blue-200",
  "IN PROGRESS": "bg-indigo-100 text-indigo-700 border-indigo-200",
  RESOLVED: "bg-emerald-100 text-emerald-700 border-emerald-200",
  PENDING: "bg-amber-100 text-amber-700 border-amber-200",
};

function KolomKontrak({ label, accent, items, total, onSelect }) {
  const fmtRp = (n) => "Rp " + Number(n ?? 0).toLocaleString("id-ID", { maximumFractionDigits: 0 });
  const head = accent === "blue" ? "text-blue-700" : "text-emerald-700";
  const footBg = accent === "blue"
    ? "bg-blue-50 border-blue-200 text-blue-800"
    : "bg-emerald-50 border-emerald-200 text-emerald-800";
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4 flex flex-col"
      data-testid={`monitor-ba-kolom-${label.toLowerCase()}`}>
      <p className={`text-xs font-bold uppercase tracking-wider mb-2.5 ${head}`}>
        {label} — {items.length} BA (In Progress)
      </p>
      {items.length === 0 ? (
        <p className="text-sm text-slate-400 py-6 text-center flex-1">Tidak ada BA berjalan.</p>
      ) : (
        <div className="space-y-2 flex-1">
          {items.slice(0, 8).map((r) => (
            <div key={r.id} data-testid={`monitor-ba-berjalan-${r.id}`}
              onClick={() => onSelect?.("ba", r)} role="button"
              className={`rounded-lg border p-2.5 flex items-center gap-3 cursor-pointer hover:ring-2 hover:ring-blue-300 transition-shadow ${
                r.terlambat ? "border-rose-300 bg-rose-50" : "border-slate-100 bg-slate-50/60"}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-800 truncate">{r.perihal}</p>
                <p className="text-[11px] text-slate-500 font-mono truncate">{r.nomor_lengkap}</p>
                <p className="text-[11px] font-semibold text-slate-600 tabular-nums">{fmtRp(r.rab_total)}</p>
              </div>
              <div className={`text-right shrink-0 ${r.terlambat ? "text-rose-600" : "text-slate-600"}`}>
                <p className="text-xl font-extrabold tabular-nums leading-none">{r.hari}<span className="text-xs font-semibold ml-0.5">hari kerja</span></p>
                {r.terlambat && (
                  <p className="text-[10px] font-bold flex items-center gap-0.5 justify-end">
                    <AlertTriangle className="h-3 w-3" /> LEWAT BATAS
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className={`mt-3 rounded-lg border px-3 py-2 flex items-center justify-between ${footBg}`}
        data-testid={`monitor-ba-total-${label.toLowerCase()}`}>
        <span className="text-[11px] font-bold uppercase tracking-wider">Total Nilai BA</span>
        <span className="text-lg font-extrabold tabular-nums">{fmtRp(total)}</span>
      </div>
    </div>
  );
}

function SlideBA({ ba, onSelect }) {
  const fmtRp = (n) => "Rp " + Number(n ?? 0).toLocaleString("id-ID", { maximumFractionDigits: 0 });
  const kontrakSorted = useMemo(() => {
    const rank = (n) => { const u = (n || "").toUpperCase(); return u.includes("MARKETING") ? 0 : u.includes("OPERASI") ? 1 : 2; };
    return [...(ba?.kontrak || [])].sort((a, b) => rank(a.nama_kontrak) - rank(b.nama_kontrak));
  }, [ba?.kontrak]);
  const inProgress = ba?.berjalan || [];
  const inKontrak = (r, kw) => (r.kontrak_nama || "").toUpperCase().includes(kw);
  const marketing = inProgress.filter((r) => inKontrak(r, "MARKETING"));
  const operasi = inProgress.filter((r) => inKontrak(r, "OPERASI"));
  const totMarketing = marketing.reduce((s, r) => s + (r.rab_total || 0), 0);
  const totOperasi = operasi.reduce((s, r) => s + (r.rab_total || 0), 0);
  return (
    <div data-testid="monitor-slide-ba" className="animate-in fade-in duration-200 space-y-4">
      <h2 className="text-lg font-extrabold text-slate-900 flex items-center gap-2">
        <FileText className="h-5 w-5 text-blue-600" /> BA PEKERJAAN
        <span className="rounded-full bg-amber-100 border border-amber-300 px-2.5 py-0.5 text-[11px] font-bold text-amber-800" data-testid="monitor-ba-filter-atcost">AT COST</span>
      </h2>
      <div className="flex flex-wrap gap-2" data-testid="monitor-ba-status">
        {Object.entries(ba?.status_counts || {}).map(([st, n]) => (
          <div key={st} className={`rounded-lg border px-4 py-2 ${BA_BADGE[st] || "bg-slate-50 border-slate-200 text-slate-600"}`}>
            <p className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{st}</p>
            <p className="text-2xl font-extrabold tabular-nums">{n}</p>
          </div>
        ))}
      </div>
      {kontrakSorted.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="monitor-ba-kontrak">
          {kontrakSorted.map((k) => {
            const serapan = k.persen_serapan_at ?? 0;
            const kritis = serapan >= 80; // peringatan: serapan anggaran sudah 80% ke atas
            return (
              <div key={k.id} data-testid={`monitor-kontrak-${k.id}`}
                className={`rounded-xl border p-4 ${kritis
                  ? "border-rose-300 bg-rose-50 ring-2 ring-rose-200 animate-pulse"
                  : "border-emerald-200 bg-emerald-50/50"}`}>
                <p className={`text-[10px] uppercase tracking-wider font-bold truncate flex items-center gap-1 ${
                  kritis ? "text-rose-700" : "text-emerald-700"}`}>
                  {kritis && <AlertTriangle className="h-3.5 w-3.5 shrink-0" />}
                  Serapan Anggaran At Cost — {k.nama_kontrak}
                </p>
                <div className="mt-1 flex items-end justify-between gap-3">
                  <p className={`text-4xl font-extrabold tabular-nums leading-none ${
                    kritis ? "text-rose-700" : "text-emerald-700"}`}
                    data-testid={`monitor-kontrak-persen-${k.id}`}>
                    {serapan}%<span className="text-sm font-semibold text-slate-400 ml-1">terpakai</span>
                  </p>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Sisa Anggaran ({k.persen_sisa_at}%)</p>
                    <p className={`text-lg font-extrabold tabular-nums ${kritis ? "text-rose-600" : "text-slate-800"}`}
                      data-testid={`monitor-kontrak-sisa-${k.id}`}>{fmtRp(k.sisa_at)}</p>
                  </div>
                </div>
                <div className={`mt-2 h-2.5 rounded-full overflow-hidden ${kritis ? "bg-rose-100" : "bg-emerald-100"}`}>
                  <div className={`h-full transition-[width] duration-500 ${kritis ? "bg-rose-500" : "bg-emerald-500"}`}
                    style={{ width: `${Math.max(0, Math.min(100, serapan))}%` }} />
                </div>
                <p className="mt-1 text-[11px] text-slate-500">
                  Terpakai {fmtRp(k.realisasi_at)} dari anggaran {fmtRp(k.anggaran_at_cost)}
                  {kritis && <span className="ml-1 font-bold text-rose-600" data-testid={`monitor-kontrak-kritis-${k.id}`}>· ANGGARAN KRITIS!</span>}
                </p>
              </div>
            );
          })}
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-4" data-testid="monitor-ba-kontrak-kolom">
        <KolomKontrak label="MARKETING" accent="blue" items={marketing} total={totMarketing} onSelect={onSelect} />
        <KolomKontrak label="OPERASI" accent="emerald" items={operasi} total={totOperasi} onSelect={onSelect} />
      </div>
    </div>
  );
}

function SlideHelpdesk({ hd, onSelect }) {
  const tiket = (hd?.tiket || []).slice(0, 8);
  return (
    <div data-testid="monitor-slide-helpdesk" className="animate-in fade-in duration-200 space-y-4">
      <h2 className="text-lg font-extrabold text-slate-900 flex items-center gap-2">
        <Headset className="h-5 w-5 text-blue-600" /> HELPDESK — TIKET BELUM SELESAI
      </h2>
      <div className="flex flex-wrap gap-2" data-testid="monitor-helpdesk-status">
        <div className="rounded-lg border px-4 py-2 bg-slate-100 border-slate-300 text-slate-700"
          data-testid="monitor-helpdesk-total">
          <p className="text-[10px] uppercase tracking-wider font-semibold opacity-70">Total Tiket</p>
          <p className="text-2xl font-extrabold tabular-nums">{hd?.total ?? 0}</p>
        </div>
        {Object.entries(hd?.status_counts || {}).map(([st, n]) => (
          <div key={st} data-testid={`monitor-helpdesk-stat-${st}`}
            className={`rounded-lg border px-4 py-2 ${HD_BADGE[st] || "bg-slate-50 border-slate-200 text-slate-600"}`}>
            <p className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{st}</p>
            <p className="text-2xl font-extrabold tabular-nums">{n}</p>
          </div>
        ))}
      </div>
      {tiket.length === 0 ? (
        <p className="py-16 text-center text-emerald-600 font-semibold" data-testid="monitor-helpdesk-empty">
          Semua tiket sudah selesai — tidak ada antrian.
        </p>
      ) : (
        <div className="space-y-2">
          {tiket.map((t) => (
            <div key={t.id} data-testid={`monitor-helpdesk-tiket-${t.id}`}
              onClick={() => onSelect?.("helpdesk", t)} role="button"
              className={`rounded-xl border bg-white shadow-sm p-3.5 flex items-center gap-4 cursor-pointer hover:ring-2 hover:ring-blue-300 transition-shadow ${
                t.hari > 5 ? "border-rose-300" : "border-slate-200"}`}>
              <div className={`shrink-0 text-center rounded-lg px-3 py-2 ${t.hari > 5 ? "bg-rose-50" : "bg-slate-50"}`}>
                <p className={`text-3xl font-extrabold tabular-nums leading-none ${t.hari > 5 ? "text-rose-600" : "text-slate-800"}`}>{t.hari}</p>
                <p className="text-[10px] font-bold text-slate-500 uppercase">Hari Kerja</p>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm sm:text-base font-semibold text-slate-800 truncate">{t.deskripsi || "-"}</p>
                <p className="text-[11px] text-slate-500 truncate">
                  <span className="font-mono">{t.nomor_lengkap}</span> · {t.requestor || "-"}
                  {t.lokasi_gedung ? ` · ${t.lokasi_gedung}` : ""} · Submit {fmtTanggal(t.submit_date)}
                </p>
              </div>
              <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-bold ${HD_BADGE[t.status] || "bg-slate-100 border-slate-200 text-slate-600"}`}>
                {t.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SlidePM({ pm }) {
  if (!pm) return <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;
  const cats = [
    { key: "engineering", label: "Engineering", data: pm.engineering, accent: "text-blue-700" },
    { key: "housekeeping", label: "Housekeeping", data: pm.housekeeping, accent: "text-emerald-700" },
  ];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="monitor-pm-slide">
      {cats.map((c) => (
        <div key={c.key} className="rounded-xl border border-slate-200 bg-white shadow-sm p-4 flex flex-col"
          data-testid={`monitor-pm-${c.key}`}>
          <p className={`text-xs font-bold uppercase tracking-wider mb-3 ${c.accent}`}>Preventive Maintenance — {c.label}</p>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-center" data-testid={`monitor-pm-${c.key}-terlambat`}>
              <AlertTriangle className="h-5 w-5 mx-auto text-rose-600" />
              <p className="text-2xl font-bold text-rose-700 mt-1">{c.data?.terlambat ?? 0}</p>
              <p className="text-[10px] font-medium text-rose-700">Terlambat</p>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center">
              <Clock className="h-5 w-5 mx-auto text-amber-600" />
              <p className="text-2xl font-bold text-amber-700 mt-1">{c.data?.hari_ini ?? 0}</p>
              <p className="text-[10px] font-medium text-amber-700">Hari Ini</p>
            </div>
            <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 text-center" data-testid={`monitor-pm-${c.key}-validasi`}>
              <ShieldCheck className="h-5 w-5 mx-auto text-violet-600" />
              <p className="text-2xl font-bold text-violet-700 mt-1">{c.data?.menunggu_validasi ?? 0}</p>
              <p className="text-[10px] font-medium text-violet-700">Menunggu Validasi</p>
            </div>
          </div>
          <p className="text-[11px] font-semibold text-slate-500 mb-1.5">Tugas Terlambat Teratas</p>
          {(c.data?.terlambat_list || []).length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center flex-1">Tidak ada tugas terlambat. 🎉</p>
          ) : (
            <div className="space-y-1.5 flex-1">
              {c.data.terlambat_list.map((t, i) => (
                <div key={t.id || `${t.nama_objek}-${t.jenis}-${i}`} className="rounded-lg border border-rose-200 bg-rose-50/60 p-2 flex items-center gap-2 text-sm">
                  <AlertTriangle className="h-4 w-4 text-rose-500 shrink-0" />
                  <span className="font-medium text-slate-800 truncate flex-1">{t.nama_objek}{t.jenis ? ` · ${t.jenis}` : ""}</span>
                  <span className="text-xs text-slate-500 shrink-0">{t.lokasi || ""}</span>
                  <span className="text-xs font-mono text-rose-600 shrink-0">{fmtTanggal(t.jatuh_tempo)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function LiveMonitorGabungan() {
  const [ba, setBa] = useState(null);
  const [hd, setHd] = useState(null);
  const [pm, setPm] = useState(null);
  const [slide, setSlide] = useState(0);
  const [clock, setClock] = useState(new Date());
  const [updatedAt, setUpdatedAt] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [detail, setDetail] = useState(null); // { tipe: 'ba'|'helpdesk', data }
  const [pdfPreview, setPdfPreview] = useState(null); // { url, nama, judul, tipe }
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadingRab, setLoadingRab] = useState(false);

  const bukaPdfBa = async () => {
    if (!detail?.data?.id) return;
    setLoadingPdf(true);
    try {
      const res = await api.get(`/ba/${detail.data.id}/pdf`, { responseType: "blob" });
      setPdfPreview({ url: URL.createObjectURL(res.data), nama: `${detail.data.nomor_lengkap || "ba"}.pdf`,
        judul: `BA — ${detail.data.perihal || ""}`, tipe: "application/pdf" });
    } catch {
      toast.error("Gagal membuka PDF BA");
    } finally { setLoadingPdf(false); }
  };

  const bukaPdfRab = async () => {
    if (!detail?.data?.id) return;
    setLoadingRab(true);
    try {
      const res = await api.get(`/ba/${detail.data.id}/rab/pdf`, { responseType: "blob" });
      setPdfPreview({ url: URL.createObjectURL(res.data), nama: `rab_${detail.data.nomor_lengkap || "ba"}.pdf`,
        judul: `RAB — ${detail.data.perihal || ""}`, tipe: "application/pdf" });
    } catch {
      toast.error("Gagal membuka PDF RAB");
    } finally { setLoadingRab(false); }
  };

  const load = useCallback(() => {
    api.get("/ba/monitor")
      .then((res) => { setBa(res.data); setUpdatedAt(new Date()); })
      .catch((err) => { if (err.response?.status === 403) setForbidden(true); });
    api.get("/helpdesk/monitor")
      .then((res) => setHd(res.data))
      .catch((err) => { if (err.response?.status === 403) setForbidden(true); });
    api.get("/pm/monitor")
      .then((res) => setPm(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, REFRESH_MS);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    const iv = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (detail || pdfPreview) return; // jeda rotasi slide saat popup terbuka
    const iv = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), SLIDE_MS);
    return () => clearInterval(iv);
  }, [detail, pdfPreview]);

  if (forbidden) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="monitor-forbidden">
        <div className="text-center space-y-3">
          <ShieldOff className="h-10 w-10 mx-auto text-slate-300" />
          <p className="text-slate-500 text-sm">Live Monitor gabungan hanya untuk akun admin utama.</p>
          <Link to="/" className="text-blue-600 text-sm underline">Kembali ke aplikasi</Link>
        </div>
      </div>
    );
  }

  const loading = ba === null && hd === null;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="live-monitor-page">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white/90 px-5 py-4 sticky top-0 backdrop-blur shadow-sm">
        <img src="/logo-patra.png" alt="PATRA Facility Management" className="h-9 w-auto mb-3" data-testid="monitor-logo" />
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <div className="flex items-center gap-3">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
            </span>
            <div>
              <h1 className="text-lg sm:text-xl font-extrabold tracking-wide flex items-center gap-2 text-slate-900">
                <MonitorPlay className="h-5 w-5 text-blue-600" /> LIVE MONITOR BA & HELPDESK
              </h1>
              <p className="text-[11px] text-slate-500">
                Rotasi otomatis BA Pekerjaan → Helpdesk tiap 12 detik · data refresh 15 detik
                {updatedAt && <> · diperbarui <span className="text-emerald-600 font-semibold" data-testid="monitor-updated">{fmtJam(updatedAt)}</span></>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            {SLIDES.map((s, i) => (
              <button key={s.key} type="button" onClick={() => setSlide(i)}
                data-testid={`monitor-tab-${s.key}`}
                className={`h-9 px-4 rounded-full text-xs font-bold border inline-flex items-center gap-1.5 transition-colors ${
                  slide === i
                    ? "bg-blue-600 border-blue-600 text-white"
                    : "bg-white border-slate-300 text-slate-500 hover:text-slate-800"}`}>
                <s.icon className="h-3.5 w-3.5" /> {s.label}
              </button>
            ))}
            <span className="font-mono text-2xl sm:text-3xl font-bold text-slate-800 tabular-nums ml-2" data-testid="monitor-clock">
              {fmtJam(clock)}
            </span>
            <Link to="/" title="Kembali ke aplikasi" data-testid="monitor-back"
              className="h-9 w-9 rounded-md border border-slate-300 flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3" data-testid="monitor-slide-indicator">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-600">{SLIDES[slide].label}</span>
          {SLIDES.map((s, i) => (
            <span key={s.key} className={`h-2 w-2 rounded-full transition-colors ${slide === i ? "bg-emerald-500" : "bg-slate-300"}`} />
          ))}
        </div>
      </div>

      <div className="p-5">
        {loading ? (
          <div className="py-24 flex justify-center" data-testid="monitor-loading">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : slide === 0 ? (
          <SlideBA ba={ba} onSelect={(tipe, data) => setDetail({ tipe, data })} />
        ) : slide === 1 ? (
          <SlideHelpdesk hd={hd} onSelect={(tipe, data) => setDetail({ tipe, data })} />
        ) : (
          <SlidePM pm={pm} />
        )}
      </div>

      {/* Pop-up detail item yang diklik */}
      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
        <DialogContent className="max-w-md" data-testid="monitor-detail-dialog">
          <DialogHeader>
            <DialogTitle className="pr-6">
              {detail?.tipe === "ba" ? "Detail BA Pekerjaan" : "Detail Tiket Helpdesk"}
            </DialogTitle>
            <DialogDescription className="font-mono">{detail?.data?.nomor_lengkap}</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-1.5 text-sm">
              {(detail.tipe === "ba" ? [
                ["Perihal", detail.data.perihal],
                ["Status", detail.data.status],
                ["Tanggal", fmtTanggal(detail.data.tanggal)],
                ["Lama Berjalan", detail.data.hari != null ? `${detail.data.hari} hari kerja (Sabtu/Minggu & libur nasional dilewati)` : "-"],
                ["Terlambat", detail.data.terlambat ? "YA — LEWAT BATAS" : "Tidak"],
                ["RAB", detail.data.punya_rab
                  ? `${detail.data.rab_items} item — Rp ${Number(detail.data.rab_total || 0).toLocaleString("id-ID")}${detail.data.jenis_anggaran ? ` (${detail.data.jenis_anggaran})` : ""}`
                  : "Belum ada"],
              ] : [
                ["Deskripsi", detail.data.deskripsi],
                ["Status", detail.data.status],
                ["Requestor", detail.data.requestor],
                ["Lokasi/Gedung", detail.data.lokasi_gedung],
                ["Tanggal Submit", fmtTanggal(detail.data.submit_date)],
                ["Umur Tiket", detail.data.hari != null ? `${detail.data.hari} hari kerja (Sabtu/Minggu & libur nasional dilewati)` : "-"],
              ]).map(([k, v]) => (
                <div key={k} className="flex gap-2 border-b border-slate-100 pb-1.5">
                  <span className="w-32 shrink-0 text-slate-500">{k}</span>
                  <span className="font-medium text-slate-800">{v || "-"}</span>
                </div>
              ))}
              {detail.tipe === "ba" && (
                <div className="pt-2 flex justify-end gap-2 flex-wrap">
                  {detail.data.punya_rab && (
                    <Button variant="outline" onClick={bukaPdfRab} disabled={loadingRab}
                      data-testid="monitor-detail-print-rab"
                      className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                      {loadingRab ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
                      Lihat / Print RAB
                    </Button>
                  )}
                  <Button onClick={bukaPdfBa} disabled={loadingPdf}
                    data-testid="monitor-detail-print-pdf"
                    className="gap-2 bg-blue-600 hover:bg-blue-700 text-white">
                    {loadingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
                    Lihat / Print PDF
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <FilePreviewDialog file={pdfPreview} onClose={() => {
        if (pdfPreview?.url) URL.revokeObjectURL(pdfPreview.url);
        setPdfPreview(null);
      }} />
    </div>
  );
}
